/**
 * EN↔ZH Language Pair Definition
 *
 * Defines resources for English and Chinese separately.
 * buildProfile() generates directional profiles:
 *   enZh = ZH native → learn EN (UI in Chinese)
 *   zhEn = EN native → learn ZH (UI in English)
 */

import type { LanguagePairDefinition, LanguageProfile } from '../types'
import { buildProfile } from '../build-profile'

const PAIR: LanguagePairDefinition = {
  id: 'en-zh',
  languages: {
    en: {
      lang: 'en',
      langName: 'English',
      shortCode: 'EN',
      scriptSample: 'Hello',
      phonetic: {
        type: 'ipa',
        fields: [
          { key: 'phonetic_us', label: 'US' },
          { key: 'phonetic_uk', label: 'UK' },
        ],
      },
      tts: {
        voices: [
          { accent: 'us', lang: 'en-US' },
          { accent: 'uk', lang: 'en-GB' },
        ],
        detectScript: /^$/,
      },
      ui: {
        inputPlaceholder: 'Enter words, phrases, or questions...',
        headingText: 'What do you want to learn today?',
        intentChips: [
          { label: 'List', intent: 'BATCH_LIST', suffix: 'how to say in Chinese' },
          { label: 'Compare', intent: 'CONFUSABLE_PAIRS', type: 'compare', separator: 'vs' },
        ],
      },
      promptConfig: {
        classifierHint: 'User may input in English.',
      },
    },
    zh: {
      lang: 'zh',
      langName: 'Chinese',
      shortCode: 'CN',
      scriptSample: '你好',
      phonetic: {
        type: 'pinyin',
        fields: [
          { key: 'phonetic_us', label: 'Pinyin' },
        ],
      },
      tts: {
        voices: [
          { accent: 'cn', lang: 'zh-CN' },
        ],
        detectScript: /[\u4e00-\u9fff\u3400-\u4dbf]/,
      },
      ui: {
        inputPlaceholder: '输入单词、句子、或问题...',
        headingText: '今天想学什么？',
        intentChips: [
          { label: '批量列表', intent: 'BATCH_LIST', suffix: '的英语怎么说' },
          { label: '对比词', intent: 'CONFUSABLE_PAIRS', type: 'compare', separator: 'vs' },
        ],
      },
      promptConfig: {
        classifierHint: 'User may input in Chinese (simplified/traditional characters).',
        languageHints: [
          'For nouns, always include the correct measure word (classifier) in the example sentence (e.g. 一只猫, 一本书).',
          'Pinyin must include tone marks (e.g. mā, má, mǎ, mà). Do NOT use tone numbers (e.g. ma1 is wrong).',
          'If a character has traditional/simplified variants, prefer simplified unless context indicates otherwise.',
          'For verbs, show the most common complement structure in the example.',
        ].join('\n'),
      },
      cardMeta: {
        keys: ['measureWord', 'measureWordPinyin', 'variants'],
        promptDescription: `"card_meta" (JSON object, required): Chinese-specific metadata:
- "measureWord": classifier word (e.g. "本", "只", "条"). Only for nouns.
- "measureWordPinyin": pinyin for the measure word with tone marks. Only with measureWord.
- "variants": object with "simplified" and "traditional" keys. Only when simplified ≠ traditional.`,
      },
    },
  },
}

export const enZhPair = PAIR
export const enZh: LanguageProfile = buildProfile(PAIR, 'en')
export const zhEn: LanguageProfile = buildProfile(PAIR, 'zh')
